import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// Token maps — values change per theme.
// Components import this and call t[token] for their colors.
export const light = {
  bg: '#f8fafc',
  bgGradient: 'linear-gradient(to bottom, #f8fafc, #f1f5f9)',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  border: '#e5e7eb',
  borderLight: '#f3f4f6',
  text: '#1f2937',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  textSlate: '#334155',
  navActive: '#eff6ff',
  navActiveText: '#2563eb',
  navActiveIcon: '#2563eb',
  itemSelected: '#1e293b',
  itemSelectedText: '#ffffff',
  avatarBg: '#dbeafe',
  avatarText: '#2563eb',
  inputBorder: '#e2e8f0',
  inputBg: '#ffffff',
  cardShadow: '0 1px 3px rgba(0,0,0,0.1)',
  dropdownShadow: '0 4px 16px rgba(0,0,0,0.10)',
  chipBg: '#f1f5f9',
  chipText: '#334155',
  danger: '#ef4444',
  dangerBg: '#fef2f2',
  dangerBorder: '#fca5a5',
  success: '#047857',
  successBg: '#d1fae5',
  spinning: 'spin 1s linear infinite',
};

export const dark = {
  bg: '#0f172a',
  bgGradient: 'linear-gradient(to bottom, #0f172a, #1e293b)',
  surface: '#1e293b',
  surfaceAlt: '#0f172a',
  border: '#334155',
  borderLight: '#1e293b',
  text: '#f1f5f9',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  textSlate: '#cbd5e1',
  navActive: '#1e3a5f',
  navActiveText: '#60a5fa',
  navActiveIcon: '#60a5fa',
  itemSelected: '#0f172a',
  itemSelectedText: '#ffffff',
  avatarBg: '#1e3a5f',
  avatarText: '#60a5fa',
  inputBorder: '#334155',
  inputBg: '#0f172a',
  cardShadow: '0 1px 3px rgba(0,0,0,0.4)',
  dropdownShadow: '0 4px 16px rgba(0,0,0,0.4)',
  chipBg: '#334155',
  chipText: '#cbd5e1',
  danger: '#f87171',
  dangerBg: '#450a0a',
  dangerBorder: '#7f1d1d',
  success: '#34d399',
  successBg: '#064e3b',
  spinning: 'spin 1s linear infinite',
};

export function useTokens() {
  const { theme } = useTheme();
  return theme === 'dark' ? dark : light;
}
